import type {
	GistStreamingChunk,
	Gist,
	GistInfo,
	GistPopulated,
	NotePopulated,
	Share,
	ApiResponse,
	UniqueId,
} from "@vita-learning/types";

import { makeUrl, revalidate } from "~/shared/utils";

import { type HttpConfig, aiHttpClient, resHttpClient } from "../../config";
import { assertCorrectUrlEndpoint } from "../../utils";

import { decodeGist } from "./helpers";

export const createGist = async (
	gist: GistInfo | Gist
): Promise<GistPopulated> => {
	const resp = await resHttpClient.post<ApiResponse<GistPopulated>>(
		"/gists",
		gist
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	await revalidate("gists");

	return decodeGist(resp.data);
};

export const createManyGists = async (
	gists: (GistInfo | Gist)[]
): Promise<GistPopulated[]> => {
	const resp = await resHttpClient.post<ApiResponse<GistPopulated[]>>(
		"/gists?bulk=true",
		gists
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	await revalidate("gists");

	return resp.data.map(decodeGist);
};

export const createGistShareUrl = async (
	payload: UniqueId
): Promise<{ url: string }> => {
	const resp = await resHttpClient.post<ApiResponse<{ url: string }>>(
		"/gists/share-urls",
		payload
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	return resp.data;
};

export const getGist = async (
	id: string,
	config?: HttpConfig
): Promise<GistPopulated> => {
	const resp = await resHttpClient.get<ApiResponse<GistPopulated>>(
		`/gists/${id}`,
		config
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	return decodeGist(resp.data);
};

export const fetchGists = async (
	url: string,
	config?: HttpConfig
): Promise<GistPopulated[]> => {
	assertCorrectUrlEndpoint(url, "/gists");

	const resp = await resHttpClient.get<ApiResponse<GistPopulated[] | null>>(
		url,
		config
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	return (resp.data || []).map(decodeGist);
};

export const updateGist = async (gist: GistInfo): Promise<GistPopulated> => {
	if (!gist.id) throw new Error("Gist ID is required");

	const resp = await resHttpClient.put<ApiResponse<GistPopulated>>(
		`/gists/${gist.id}`,
		gist
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	await revalidate("gists");

	return decodeGist(resp.data);
};

export const toggleGist = async (id: string): Promise<void> => {
	const resp = await resHttpClient.patch<ApiResponse<void>>(`/gists/${id}`);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	await revalidate("gists");

	return resp.data;
};

export const shareGist = async (
	id: string,
	share: Share
): Promise<GistPopulated> => {
	const resp = await resHttpClient.patch<ApiResponse<GistPopulated>>(
		`/gists/${id}/share`,
		{ ...share, id }
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	await revalidate("gists");

	return decodeGist(resp.data);
};

export const deleteGist = async (id: string): Promise<void> => {
	const resp = await resHttpClient.delete<ApiResponse<void>>(`/gists/${id}`);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	await revalidate("gists");
	return resp.data;
};

export const generateGist = async (
	note: Partial<Gist> &
		(
			| {
					file?: {
						id: string;
						userId: string;
						title: string;
						ext: string;
						url: string;
						expiresAt: string;
					};
			  }
			| {
					note?: NotePopulated;
			  }
			| {
					url?: string;
			  }
		),
	source: "file" | "note" | "url",
	onStream: (chunk: GistStreamingChunk) => void
) => {
	const url = makeUrl("/gists");

	url.searchParams.set("source", source);

	await aiHttpClient.post<void>(url.pathname + url.search, note, {
		headers: {
			// Accept server-sent events
			Accept: "text/event-stream",
			// Disabling compression for streaming responses
			"Accept-Encoding": "identity",
			// Sending the data as JSON
			"Content-Type": "application/json",
			// Preventing caching
			"Cache-Control": "no-cache",
		},
		cache: "no-cache",
		onStream: chunk => {
			// Chunks might accumulate so split them by the new line
			const lines = chunk.split("\n").filter(line => line.trim() !== "");
			const DATA_CHUNK_PREFIX = "data: ";
			const ERROR_CHUNK_PREFIX = "error: ";

			lines.forEach(line => {
				try {
					if (line.startsWith(DATA_CHUNK_PREFIX))
						onStream(
							JSON.parse(
								line.replace(DATA_CHUNK_PREFIX, "").trim()
							) as GistStreamingChunk
						);

					if (line.startsWith(ERROR_CHUNK_PREFIX))
						throw JSON.parse(line.replace(ERROR_CHUNK_PREFIX, "").trim());
				} catch (e) {
					if (!(e instanceof SyntaxError)) throw e;

					console.error(e);
				}
			});
		},
	});
};
